import { Button, Grid, Select, Stack, Text, TextInput } from "@mantine/core";
import _ from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { IState } from "../../../../data/reducers/state";
import { IAssociation } from "../../../../types/association/Association";
import {
  IShift,
  OrderingType,
  ShiftDaysOfWeek,
} from "../../../../types/shifts/Shift";
import { toDateTime, toDateTimeNullable } from "../../../../util/date+helper";
import AssociationSelector from "../../Association/AssociationSelector";

interface ShiftEditorOverviewTabProps {
  isNew: boolean;
  associations: IAssociation[];
  shift: IShift;
  onEdit: (name: string, value: any) => void;
}

interface ValidatedInputProps {
  validate: (value: any) => boolean;
  onFinal: (value: any) => void;
  label: string;
  initialValue?: any;
  defaultValue?: any;
  type?: string;
  min?: string;
  max?: string;
}

const ValidatedInput: React.FunctionComponent<any> = ({
  initialValue,
  onFinal,
  validate,
  ...props
}) => {
  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [innerValue, setInnerValue] = React.useState(initialValue);

  const _onChange = (newValue) => {
    const isValid = validate(newValue);
    setIsValid(isValid);
    setInnerValue(newValue);
    return isValid;
  };

  const _onFinal = (newValue) => {
    const isValid = validate(newValue);
    setIsValid(isValid);

    if (isValid) onFinal(newValue);
  };

  const accentColor = isValid
    ? "var(--color-text-medium)"
    : "var(--color-danger-text)";
  const backgroundColor = isValid
    ? "var(--color-bg)"
    : "var(--color-danger-bg)";

  return (
    <Stack gap="xs">
      <Text
        size="sm"
        fw={500}
        style={{
          color: accentColor,
          backgroundColor,
        }}
      >
        {props.label}
      </Text>
      <TextInput
        style={{
          borderColor: accentColor,
        }}
        type={props.type}
        value={innerValue}
        onChange={(e) => _onChange(e.currentTarget.value)}
        onBlur={(e) => _onFinal(e.currentTarget.value)}
      />
    </Stack>
  );
};

const ShiftEditorOverviewTab: React.FunctionComponent<
  ShiftEditorOverviewTabProps
> = ({ shift, onEdit, isNew, associations }) => {
  const [changes, setChanges] = React.useState<Partial<IShift>>({});
  const derivedShift = { ...shift, ...changes };

  const lastShiftSession = shift.sessions?.[shift.sessions?.length - 1];
  const shiftTimeMin = toDateTimeNullable(lastShiftSession?.start)?.toFormat("HH:mm");
  const shiftTimeMax = toDateTimeNullable(lastShiftSession?.finish)?.toFormat("HH:mm");

  return (
    <div>
      {isNew && (
        <AssociationSelector
          value={shift.associationId}
          onChange={
            (association) => null /* onEdit('associationId', association._id) */
          }
          associations={associations || []}
          disabled={!isNew}
        />
      )}
      {!isNew && (
        <div className="mb-3">
          <label className="form-label"><strong>Association:</strong></label>
          <div className="text-muted">
            {associations?.find(a => a._id === shift.associationId)?.name || 'Unknown Association'}
          </div>
        </div>
      )}
      <Stack gap="xs">
        <Text size="sm" fw={500}>Name</Text>
        <TextInput
          name="name"
          onChange={(e) => setChanges({ name: e.currentTarget.value })}
          onKeyDown={(e) => {
            if (e.key == "Enter") onEdit("name", derivedShift.name);
          }}
          onBlur={() => onEdit("name", derivedShift.name)}
          value={derivedShift.name || ""}
        />
      </Stack>
      <Stack gap="xs">
        <Text size="sm" fw={500}>Shift Grid View</Text>
        <Select
          name="gridView"
          onChange={(value) => onEdit("gridView", value)}
          value={derivedShift.gridView || "venue"}
          data={[
            { value: "venue", label: "Venue" },
            { value: "competition", label: "Competition" },
          ]}
        />
      </Stack>
      <hr />
      <Stack gap="xs">
        <Text size="sm" fw={500}>Shift ordering</Text>
        <Select
          name="shiftOrderingType"
          onChange={(value) => {
            const newType = value;
            let newPriority = derivedShift?.ordering?.value || 100;
            if (newType == OrderingType.dayOfWeek) {
              // find day of week in shift name
              const guessedDayOfWeek =
                ShiftDaysOfWeek.guessDayOfWeekInString(derivedShift.name) ||
                ShiftDaysOfWeek.Monday;
              newPriority =
                ShiftDaysOfWeek.getShiftPriorityFromDayOfWeek(guessedDayOfWeek);
            }

            onEdit("ordering", { type: newType, value: newPriority });
          }}
          value={derivedShift.ordering?.type || OrderingType.custom}
          data={OrderingType.all().map((ot) => ({
            value: ot,
            label: OrderingType.toDisplayString(ot),
          }))}
        />
      </Stack>
      <Stack gap="xs">
        {(derivedShift.ordering?.type || OrderingType.custom) ==
          OrderingType.custom && (
            <>
              <Text size="sm" fw={500}>
                Priority (lower priority is higher in the list (noting: Mo = 10,
                Tu=20, ...))
              </Text>
              <TextInput
                name="shiftOrderingPriority-custom"
                type="number"
                onChange={(e) =>
                  onEdit("ordering", {
                    ...(derivedShift.ordering || {}),
                    value: parseInt(e.currentTarget.value),
                  })
                }
                value={derivedShift.ordering?.value || 100}
              />
            </>
          )}
        {derivedShift.ordering?.type == OrderingType.dayOfWeek && (
          <>
            <Text size="sm" fw={500}>Day of week</Text>
            <Select
              name="shiftOrderingPriority-day-of-week"
              onChange={(value) =>
                onEdit("ordering", {
                  ...(derivedShift.ordering || {}),
                  value: parseInt(value),
                })
              }
              value={String(derivedShift.ordering?.value || 70)}
              data={ShiftDaysOfWeek.all().map((day) => ({
                value: String(ShiftDaysOfWeek.getShiftPriorityFromDayOfWeek(day)),
                label: ShiftDaysOfWeek.toString(day),
              }))}
            />
          </>
        )}
      </Stack>
      <hr />
      <Stack gap="sm">
        <p>
          <span>
            Default supervisor times
            <Button
              // pin to right of <p> label above
              style={{ position: "absolute", right: "60px" }}
              onClick={() =>
                onEdit("supervisorDefaultTimes", [
                  ...(shift.supervisorDefaultTimes || []),
                  {
                    // default to max finish time of previous super (or shift start)
                    start: _.max([
                      toDateTime(shift.start).toFormat("HH:mm"),
                      ...(shift.supervisorDefaultTimes?.map((s) => s.finish) ||
                        []),
                    ]),
                    finish: toDateTime(shift.finish).toFormat("HH:mm"),
                  },
                ])
              }
            >
              Add
            </Button>
          </span>
        </p>
        {(shift.supervisorDefaultTimes || []).map((defaultRange, idx) => (
          <div
            key={`default-supervisor-times-${defaultRange.start}-${defaultRange.finish}`}
            style={{
              padding: "10px",
              backgroundColor:
                idx % 2 == 1 ? "var(--color-bg)" : "var(--color-bg-card)",
            }}
          >
            <Button
              variant="outline"
              size="sm"
              style={{ position: "absolute", right: "60px", padding: "4px" }}
              onClick={() =>
                onEdit(
                  "supervisorDefaultTimes",
                  shift.supervisorDefaultTimes?.filter(
                    (r, _idx) => idx !== _idx,
                  ),
                )
              }
            >
              {" "}
              Remove{" "}
            </Button>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <ValidatedInput
                  label="Start"
                  validate={(value) => !!value.match(/^[0-2][0-9]:[0-6][0-9]$/)}
                  min={shiftTimeMin}
                  max={shiftTimeMax}
                  onFinal={(value) => {
                    const newElement = { ...defaultRange, start: value };
                    onEdit(
                      "supervisorDefaultTimes",
                      shift.supervisorDefaultTimes?.map((r, _idx) =>
                        idx == _idx ? newElement : r,
                      ),
                    );
                  }}
                  defaultValue={defaultRange.start}
                  type="time"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <ValidatedInput
                  label="Finish"
                  validate={(value) => !!value.match(/^[0-2][0-9]:[0-6][0-9]$/)}
                  min={defaultRange.start}
                  max={shiftTimeMax}
                  onFinal={(value) => {
                    const newElement = { ...defaultRange, finish: value };
                    onEdit(
                      "supervisorDefaultTimes",
                      shift.supervisorDefaultTimes?.map((r) =>
                        r == defaultRange ? newElement : r,
                      ),
                    );
                  }}
                  defaultValue={defaultRange.finish}
                  type="time"
                />
              </Grid.Col>
            </Grid>
          </div>
        ))}
      </Stack>
    </div>
  );
};

function mapStateToProps(state: IState, ownProps) {
  return {
    associations: state.associations.admin.map(
      (a) => state.associations.map[a],
    ),
  };
}

export default connect(mapStateToProps)(ShiftEditorOverviewTab);
